import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaBriefcase,
  FaShoppingCart,
  FaEye,
  FaRocket,
  FaMobileAlt,
  FaStar,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const ScalableEcommerceWebsitesInDIFC = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Scalable E-commerce Websites in DIFC" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Grow Without Limits</Subheading>}
        heading={
          <Heading>
            Scalable <span tw="text-primary-500">E-commerce Websites</span> in
            DIFC
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we build scalable e-commerce websites in **Dubai
            International Financial Centre (DIFC)** that grow with your
            business. Our solutions combine flexibility, performance, and DIFC’s
            business edge to ensure long-term success.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // E-commerce dashboard
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Business growth chart
          "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Dubai skyline
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Scale Your Store"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Scalable E-commerce Solutions in DIFC"
        description="We design websites ready for growth and performance."
        cards={[
          {
            icon: <FaBriefcase color="#ff5464" size={40} />,
            title: "Future-Proof Design",
            description: "Built to handle increasing traffic and sales.",
          },
          {
            icon: <FaShoppingCart color="#ff5464" size={40} />,
            title: "Flexible Architecture",
            description: "Adapts to new features and integrations.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Seamless User Experience",
            description: "Engaging designs that retain customers.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Optimized Performance",
            description: "Fast-loading stores for higher conversions.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Cross-Device Support",
            description: "Responsive for all platforms and devices.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted in DIFC",
            description: "Proven scalability for DIFC businesses.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Scalable E-commerce Process"
        description={
          <Description>
            We create scalable e-commerce websites with a strategic approach:
            <ul tw="mt-4 list-disc pl-6">
              <li>Strategy: Plan for scalability and growth.</li>
              <li>Design: Craft intuitive, scalable layouts.</li>
              <li>Development: Build with modular, robust code.</li>
              <li>Testing: Validate performance under load.</li>
              <li>Launch: Deploy a store ready to scale.</li>
              <li>Support: Ensure ongoing adaptability and updates.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Team planning
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Business strategy
          "https://images.unsplash.com/photo-1531973576160-7125cd663d86?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Dubai business district
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Our Achievements</Subheading>}
        heading="Scalable Success in DIFC"
        description="Our scalable websites drive measurable growth."
        cards={[
          {
            icon: <FaShoppingCart color="#ff5464" size={40} />,
            title: "Sales Expansion",
            description: "Supported a DIFC retailer’s rapid growth.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Increased Traffic",
            description: "Scaled a store to handle high demand.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Confidence",
            description: "Trusted for scalability by DIFC brands.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Grow?</Subheading>}
        heading="Scalable E-commerce in DIFC"
        description={
          <Description>
            Partner with MilaajBrandset to build scalable e-commerce websites in
            DIFC that evolve with your business. Contact us today to create a
            store built for growth!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Mobile e-commerce
          "https://images.unsplash.com/photo-1526458179558-6c36b2399722?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Developer coding
          "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60", // Dubai modern architecture
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
